<script lang="ts" setup>
import type { HeroContentProp } from '@nimbus/types'
import type { UIButtonProps } from '@core/components/UI/Button.vue'
import type { ConfiguredImageProviders } from '@nuxt/image'
import { GameAssetTypes, type GameClickEventEmit } from '@game/types'
import { computed, ref, useAssetFallback, useGameAssets, useHeroGame, useHeroMedia, useImage, useNetwork, useThemeIllustration, useThemeLogo, useUserInfo, watch } from '#imports'

const props = defineProps<HeroContentProp>()
const emit = defineEmits<GameClickEventEmit>()

const { game, isClosed, gameLoading, startGame, cta, isMobile } = useHeroGame(() => props.content)

const { getHeroGatewayUrl } = useHeroMedia()
const { assetUrl, videoAssetUrl, hasAsset, firstAvailable } = useGameAssets()
const brandIcon = useThemeLogo('icon')
const brandTileBackground = useThemeIllustration('tileBackground')
const brandHeroBackground = useThemeIllustration('heroBackground')
const { isLoggedIn } = useUserInfo()
const { effectiveType } = useNetwork()
const img = useImage()

const cmsPoster = computed(() =>
    props.content?.video_poster_src ? getHeroGatewayUrl(props.content.video_poster_src, props.preset) : null,
)

const TILE_VIDEO_WIDTH = 720
const TILE_VIDEO_HEIGHT = 344
const TILE_VIDEO_COMPACT_WIDTH = 640
const TILE_VIDEO_COMPACT_HEIGHT = 306
const TILE_VIDEO_QUALITY = 78

const {
    src: heroLogoSrc,
    isFallback: heroLogoIsFallback,
    onError: onLogoError,
    reset: resetHeroLogoError,
} = useAssetFallback(
    () => firstAvailable(
        game.value,
        ...(isLoggedIn.value ? [GameAssetTypes.LOGO_AFTER] : []),
        GameAssetTypes.FEATURED_LOGO,
        GameAssetTypes.LOGO,
    ),
    () => brandIcon.value!.src,
)

const {
    src: tilePosterSrc,
    isFallback: tilePosterIsFallback,
    onError: onTilePosterError,
    reset: resetTilePosterError,
} = useAssetFallback(
    () => cmsPoster.value
        ?? firstAvailable(
            game.value,
            GameAssetTypes.HERO_VIDEO_POSTER,
            GameAssetTypes.VIDEO_SMALL_POSTER,
            GameAssetTypes.TILE,
        ),
    () => brandTileBackground.value!.src,
)

const {
    src: desktopTileBgSrc,
    isFallback: desktopTileBgIsFallback,
    onError: onDesktopTileBgError,
    reset: resetDesktopTileBgError,
} = useAssetFallback(
    () => firstAvailable(game.value, GameAssetTypes.HERO_BACKGROUND, GameAssetTypes.TILE),
    () => brandHeroBackground.value!.src,
)

const heroVideoSrc = computed<string | null>(() => {
    if (props.content?.video_src) return props.content.video_src
    const w = isMobile ? TILE_VIDEO_COMPACT_WIDTH : TILE_VIDEO_WIDTH
    const h = isMobile ? TILE_VIDEO_COMPACT_HEIGHT : TILE_VIDEO_HEIGHT
    if (hasAsset(game.value, GameAssetTypes.HERO_VIDEO))
        return videoAssetUrl(game.value, GameAssetTypes.HERO_VIDEO, w, h, TILE_VIDEO_QUALITY)
    if (hasAsset(game.value, GameAssetTypes.VIDEO))
        return videoAssetUrl(game.value, GameAssetTypes.VIDEO, w, h, TILE_VIDEO_QUALITY)
    return null
})

const videoError = ref(false)
function onVideoError() {
    videoError.value = true
}

const videoIntent = computed(() => {
    const slowConnection = effectiveType.value && ['slow-2g', '2g'].includes(effectiveType.value)
    return !!props.content?.enable_video && !slowConnection && !videoError.value
})

const videoConfirmed = computed(() =>
    videoIntent.value
    && !tilePosterIsFallback.value
    && (!!props.content?.video_id || heroVideoSrc.value !== null),
)

watch(() => game.value?.slug, () => {
    videoError.value = false
    resetHeroLogoError()
    resetTilePosterError()
    resetDesktopTileBgError()
})

const videoPosterUrl = computed(() => {
    if (tilePosterIsFallback.value) return tilePosterSrc.value
    const isCms = !!cmsPoster.value && tilePosterSrc.value === cmsPoster.value
    return img(
        tilePosterSrc.value,
        { width: 720, quality: 70 },
        {
            preset: isCms ? props.preset : 'gameservice',
            provider: isCms && props.preset === 'local' ? undefined : 'cloudflare' as keyof ConfiguredImageProviders,
        },
    )
})

// Match the desktop tile bg <NuxtImg> exactly so the browser dedupes the fetch.
const tileTexture = computed(() => {
    if (!hasAsset(game.value, GameAssetTypes.TILE)) return null
    return img(
        assetUrl(game.value, GameAssetTypes.TILE),
        { width: 1306, quality: 60 },
        { preset: 'gameservice', provider: 'cloudflare' as keyof ConfiguredImageProviders },
    )
})

// Normalize empty string → '#000' — an empty CMS value is treated as "set" by
// CSS var(), defeating the default fallback.
const bgSolid = computed(() => props.content?.gradient_outer_color || '#000')

async function handleStart() {
    await startGame(g => emit('game-click', g))
}
</script>

<template>
    <div
        data-test="hero"
        class="hero hero--compact"
        :class="{ 'hero--video-confirmed': videoConfirmed }"
        :style="{
            '--bg-solid': bgSolid,
            '--tile-texture': tileTexture ? `url('${tileTexture}')` : null,
        }"
    >
        <div class="hero__gradient fit-parent hero__gradient--compact" />
        <div class="hero__content hero__content--compact">
            <NuxtImg
                class="hero__logo--compact"
                provider="cloudflare"
                :preset="heroLogoIsFallback ? 'local' : 'gameservice'"
                :src="heroLogoSrc"
                :alt="`${game?.name} logo`"
                width="220"
                quality="50"
                densities="1x"
                @error="onLogoError"
            />
            <div class="hero__headline">
                <!-- eslint-disable vue/no-v-html content is safe -->
                <p v-if="content.title" class="hero__title" v-html="content.title" />
                <!-- eslint-enable vue/no-v-html -->
            </div>

            <div class="hero__action hero__action--compact">
                <UIButton
                    as="cta"
                    class="hero__cta hero__cta--compact"
                    :class="{ 'hero__cta--no-text-shadow': !!content.button_text_color }"
                    size="xl"
                    :color="content.button_color as UIButtonProps['color']"
                    :text-color="content.button_text_color as UIButtonProps['textColor']"
                    :to="content.button_url"
                    @click.prevent="handleStart"
                >
                    <template v-if="gameLoading">
                        <UILoader :color="false" width="40px" height="40px" />
                    </template>

                    <template v-else>
                        {{ !isClosed ? content.button_text : isClosed }}
                    </template>

                    <template v-if="cta && cta.icon" #append>
                        <UIIcon
                            :name="cta.icon"
                            :size="cta.size"
                            color="inherit"
                        />
                    </template>
                </UIButton>
            </div>
        </div>
        <div
            class="hero__game-tile hero__game-tile--compact"
            @click.prevent="handleStart"
        >
            <div
                class="hero__game-tile__ratio"
                :class="{ 'hero__game-tile__ratio--iframe': videoIntent && content.video_id }"
            />
            <NuxtImg
                v-if="isMobile"
                class="hero__media hero__media--poster"
                provider="cloudflare"
                :preset="tilePosterIsFallback ? 'local' : 'gameservice'"
                :src="tilePosterSrc"
                sizes="calc(100vw - 30px) sm:352px lg:500px"
                :alt="game?.name"
                width="720"
                quality="50"
                densities="1x"
                @error="onTilePosterError"
            />
            <NuxtImg
                v-else
                class="hero__media hero__media--poster"
                provider="cloudflare"
                :preset="desktopTileBgIsFallback ? 'local' : 'gameservice'"
                :src="desktopTileBgSrc"
                :alt="game?.name"
                width="1306"
                quality="60"
                densities="1x"
                @error="onDesktopTileBgError"
            />
            <UIIframe
                v-if="videoIntent && content.video_id"
                class="hero__media hero__media--iframe"
                :class="{ 'hero__media--visible': videoConfirmed }"
                :video-id="content.video_id"
                :title="game?.name"
                :fit-parent="true"
                :aspect-ratio="null"
                loading="eager"
            />
            <video
                v-else-if="videoIntent && heroVideoSrc"
                class="hero__media hero__media--video"
                :class="{ 'hero__media--visible': videoConfirmed }"
                autoplay
                loop
                muted
                playsinline
                disableRemotePlayback
                decoding="async"
                preload="metadata"
                :poster="videoPosterUrl"
                :src="heroVideoSrc"
                :title="game?.name"
                @error="onVideoError"
            />
            <div class="hero__tile-gradient" />
            <NuxtImg
                class="hero__tile-logo"
                provider="cloudflare"
                :preset="heroLogoIsFallback ? 'local' : 'gameservice'"
                :src="heroLogoSrc"
                :alt="`${game?.name} logo`"
                width="220"
                quality="50"
                densities="1x"
                @error="onLogoError"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.hero {
    display: flex;
    flex-direction: column-reverse;
    position: relative;
    padding: 0;
    color: #fff;
    overflow: hidden;
    background-position: top center;
    background-repeat: no-repeat;
    background-size: cover;
    width: 100%;

    @include tablet(md) {
        padding: 0;
    }

    &--compact {
        padding: 0;
        background: var(--bg-solid, #000) !important;

        @include mobile {
            justify-content: flex-end;
        }

        @include tablet(md) {
            padding: 0;
            height: 344px;
            max-height: 344px;
            background: var(--bg-solid, #000) !important;

            &::before {
                content: '';
                position: absolute;
                inset: 0;
                background: var(--tile-texture, none) center / cover no-repeat;
                opacity: 0.5;
                z-index: 1;
                pointer-events: none;
            }
        }

        &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 50%;
            background: var(--hero-bottom-gradient-mobile, none);
            pointer-events: none;
            z-index: 0;

            @include tablet(md) {
                background: var(--hero-bottom-gradient, none);
                z-index: 3;
            }
        }
    }

    &__logo--compact {
        display: none;

        @include tablet(md) {
            display: block;
            width: 140px;
            height: auto;
            max-height: 120px;
            margin: 0 0 8px;
            object-fit: contain;
            filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
        }
    }

    &__gradient--compact {
        display: none;

        @include tablet(md) {
            display: block;
            position: absolute;
            inset: 0;
            z-index: 3;
            pointer-events: none;
            background:
                linear-gradient(to right, var(--bg-solid) 0%, transparent max(0px, calc(100% - 744px))),
                linear-gradient(to left, var(--bg-solid) 0%, transparent 18%);
        }

        @include desktop {
            background:
                linear-gradient(to right, var(--bg-solid) 0%, transparent max(0px, calc(100% - 870px))),
                linear-gradient(to left, var(--bg-solid) 0%, transparent 18%);
        }
    }

    &__content {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        z-index: 1;

        @media (max-width: #{ $screen-md - 1 }) {
            align-items: center;
        }

        @include tablet(md) {
            width: 50%;
        }

        &--compact {
            padding-bottom: 52px;

            @include mobile {
                height: auto;
                gap: 8px;
                padding: 8px 15px 28px;
                background: var(--bg-solid, #000);
            }

            @include tablet(md) {
                position: absolute;
                z-index: 4;
                display: flex;
                flex-direction: column;
                align-items: center;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: auto;
                max-width: 50%;
                height: auto;
                padding: 28px 0 24px;
                background: none;
            }
        }
    }

    &--video-confirmed {
        .hero__content--compact {
            @include tablet(md) {
                top: 100%;
                left: 12%;
                transform: translate(0, -100%);
                align-items: flex-start;
                padding: 0 0 52px;
                transition:
                    top 0s 200ms,
                    left 0s 200ms,
                    transform 0s 200ms,
                    padding 0s 200ms,
                    align-items 0s 200ms;
                animation: hero-compact-pose-fade 400ms ease;

                @media (prefers-reduced-motion: reduce) {
                    animation: none;
                    transition: none;
                }

                .hero__title {
                    text-align: left;
                    transition: text-align 0s 200ms;

                    @media (prefers-reduced-motion: reduce) {
                        transition: none;
                    }
                }

                .hero__action--compact {
                    align-items: flex-start;
                    transition: align-items 0s 200ms;

                    @media (prefers-reduced-motion: reduce) {
                        transition: none;
                    }
                }
            }
        }
    }

    &__headline {
        @media (max-width: #{ $screen-md - 1 }) {
            text-align: center;
        }
    }

    &__title {
        position: relative;
        margin: 0 0 5px;
        color: $hero-game-title-color;
        font-size: 8vw;
        font-size: clamp(22px, 8vw, 30px);
        font-family: $brand-font-family;
        font-weight: 900;
        line-height: 1.1;
        text-align: center;
        text-shadow: $hero-headings-text-shadow;

        @include mobile {
            font-size: 9vw;
            font-size: clamp(22px, 9vw, 32px);
        }

        @include tablet(md) {
            margin: 0 0 20px;
        }

        @include desktop {
            font-size: 4vw;
            font-size: clamp(22px, 4vw, 34px);
        }

        @include desktop-xl {
            font-size: 4.4vw;
            font-size: clamp(22px, 4.4vw, 40px);
        }

        :deep(span) {
            white-space: nowrap;
        }

        :deep(sup),
        :deep(sub) {
            font-size: 10px;
            font-weight: 600;
            text-shadow: $hero-sub-sup-text-shadow;
        }
    }

    &__action {
        display: flex;
        width: 100%;
        flex: 1;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        margin-bottom: 30px;

        @media (min-width: $screen-sm) {
            align-items: flex-start;
            justify-content: flex-end;
        }

        @include tablet(md) {
            width: 90%;
        }

        @include desktop-xl {
            margin-bottom: 40px;
        }

        &:hover {
            color: inherit;
            text-decoration: none;
        }

        &--compact {
            @include mobile {
                flex: 0 0 auto;
                margin-bottom: 0;
            }

            @include tablet(md) {
                flex: 0 0 auto;
                width: auto;
                align-items: center;
                padding-top: 0;
                margin: 0;
            }
        }
    }

    &__cta {
        width: 100%;
        max-width: 300px;

        &:not(:hover, :focus) {
            transition: none;
        }

        @media (max-width: #{ $screen-md - 1 }) {
            width: calc(100% - 30px);
            max-width: 380px;
            align-self: center;
            margin-top: 5vw;
        }

        :deep(.btn__content) {
            padding: 0 15px;
            font-size: clamp($font-size-lg, 5vw, $font-size-xxl);
            white-space: nowrap;

            @include tablet {
                font-size: $font-size-lg;
            }
        }

        &--compact {
            height: 56px;
            :deep(.btn__content) {
                padding: 8px;
            }

            @include tablet(md) {
                max-width: none;
                width: auto;
                height: 60px;
            }

            @include mobile {
                max-width: 320px;
            }
        }
    }

    &__game-tile {
        position: relative;
        width: 100%;
        border-radius: var(--border-radius, $border-radius);
        margin-bottom: 20px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

        @include tablet(md) {
            max-width: 500px;
        }

        @include desktop {
            width: 527px;
        }

        &__ratio {
            padding-top: 63.125%;
            height: 0;

            @include tablet(md) {
                display: none;
            }

            &--iframe {
                padding-top: 56.25%;
            }
        }

        &--compact {
            box-shadow: none;
            padding: 0;
            border-radius: 0;

            @include mobile {
                margin-bottom: 0;
                overflow: hidden;
            }

            @include tablet(md) {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                max-width: none;
                border-radius: 0;
                margin: 0;
                z-index: 2;
            }
        }
    }

    &__media {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        object-fit: cover;
        pointer-events: none;

        &--poster {
            transition: opacity 280ms ease-out;

            @media (prefers-reduced-motion: reduce) {
                transition: opacity 120ms linear;
            }
        }

        &--video,
        &--iframe {
            opacity: 0;
            transition: opacity 320ms ease-out;

            @media (prefers-reduced-motion: reduce) {
                transition: opacity 120ms linear;
            }
        }

        &--visible {
            opacity: 1;
        }

        @include tablet(md) {
            &.hero__media--video,
            &.hero__media--iframe {
                right: 24px;
                left: auto;
                width: 720px;
            }

            &.hero__media--video {
                height: 344px;
                -webkit-mask-image: linear-gradient(
                    90deg,
                    transparent 0,
                    transparent 8%,
                    #000 40%,
                    #000 92%,
                    transparent 100%
                );
                mask-image: linear-gradient(90deg, transparent 0, transparent 8%, #000 40%, #000 92%, transparent 100%);
            }

            &.hero__media--iframe {
                height: 344px;
                aspect-ratio: 16 / 9;
                -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 24%, #000 92%, transparent 100%);
                mask-image: linear-gradient(90deg, transparent 0, #000 24%, #000 92%, transparent 100%);
            }

            &.hero__media--iframe :deep(iframe) {
                aspect-ratio: 16 / 9;
            }
        }

        @include desktop {
            &.hero__media--video,
            &.hero__media--iframe {
                right: 150px;
            }
        }
    }

    &__tile-gradient {
        position: absolute;
        bottom: -1px;
        left: 0;
        width: 100%;
        height: 50%;
        pointer-events: none;
        z-index: 1;
        background: linear-gradient(
            to bottom,
            transparent 0%,
            var(--bg-solid, rgba(0, 0, 0, 0.5)) 80%,
            var(--bg-solid, rgba(0, 0, 0, 0.5)) 100%
        );

        @include tablet(md) {
            display: none;
        }
    }

    &__tile-logo {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 45%;
        max-width: 180px;
        height: auto;
        max-height: 60%;
        object-fit: contain;
        pointer-events: none;
        z-index: 2;
        // translateZ keeps the scaled image crisp under GPU compositing.
        transform: translate(-50%, -50%) translateZ(0);

        // The tile gets noticeably wider above small-phone width, so shrink
        // the logo to stay in proportion with the poster.
        @media (min-width: $screen-sm) {
            width: 30%;
        }

        @include tablet(md) {
            display: none;
        }
    }

    &--video-confirmed &__tile-logo {
        @media (max-width: #{ $screen-md - 1 }) {
            top: 16px;
            left: 16px;
            width: 105px;
            max-width: none;
            max-height: none;
            transform: translateZ(0);
        }
    }

    :deep(.hero__cta--no-text-shadow) {
        text-shadow: none !important;
    }
}

@keyframes hero-compact-pose-fade {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0;
    }
}
</style>
